import { create } from "zustand";
import {
  CompanyCustomizationSettings,
  CompanySettings,
  CompanyStatus,
  CompanyTypes,
  CompanyVerificationStatus,
  CURRENCIES,
  CurrencyCodes,
  OwnerTypes,
} from "@fena/toolkit-types";
import api from "../util/api";
import { CurrencyItem } from "../constants/currencies";
import {
  OnboardingDetails,
  ValidateOnboardingTypes,
} from "../modules/private/settings-module/integrations/payments/utils/types";
import { useEffect } from "react";
import { FullCompanyApiType } from "../util/types";

interface CompanyStore {
  company?: FullCompanyApiType & {
    currentSubscription?: any;
    b2bSubscription?: any;
  };
  parentCustomizationSettings?: CompanyCustomizationSettings;
  companySettings?: CompanySettings;
  currencyItemsForDropdown: CurrencyItem[];
  getStripeDetails?: OnboardingDetails;
  validateStripeFields?: ValidateOnboardingTypes;
  companyCustomizationSettings?: CompanyCustomizationSettings;

  setCompany: (
    data: Partial<
      FullCompanyApiType & {
        currentSubscription?: any;
        b2bSubscription?: any;
      }
    >
  ) => void;
  clearCompany: () => void;
  refreshCompany: () => Promise<void>;
  setParentCustomizationSettings: (data: CompanyCustomizationSettings) => void;
  refreshSettings: () => Promise<void>;
  refreshStripeDetails: () => Promise<void>;
  refreshValidateStripeFields: () => Promise<void>;
  calculateCurrencies: () => void;
}

const defaultCompany: FullCompanyApiType = {
  _id: "",
  type: CompanyTypes.COMPANY,
  identifier: "",
  status: CompanyStatus.ACTIVE,
  canManageCompanies: false,
  companySettings: { ownerType: OwnerTypes.SYSTEM, owner: "" },
  companyBillingPlan: "",
  companyLimits: "",
  companyCustomizationSettings: undefined,
  isSandbox: false,
  verificationStatus: CompanyVerificationStatus.VERIFIED,
  createdAt: new Date(),
  stripeData: "",
  stripeUsageData: "",
};

export const useCompanyStore = create<CompanyStore>((set, get) => ({
  company: undefined,
  parentCustomizationSettings: undefined,
  companySettings: undefined,
  currencyItemsForDropdown: [],
  getStripeDetails: undefined,
  validateStripeFields: undefined,
  companyCustomizationSettings: undefined,

  setCompany: (data) => {
    const currentCompany = get().company ?? defaultCompany;
    set({
      company: {
        ...currentCompany,
        ...data,
        companySettings: {
          ...currentCompany.companySettings,
          ...(data.companySettings ?? {}),
        },
      },
    });
  },

  clearCompany: () => {
    set({
      company: undefined,
      currencyItemsForDropdown: [],
      getStripeDetails: undefined,
      validateStripeFields: undefined,
    });
  },

  refreshCompany: async () => {
    try {
      const data = await api.getCompany();
      const currentCompany = get().company ?? defaultCompany;
      set({
        company: {
          ...currentCompany,
          ...data,
          companySettings: {
            ...currentCompany.companySettings,
            ...(data.companySettings || {}),
          },
        },
      });
      get().calculateCurrencies();
    } catch (error) {
      console.error("Error refreshing company:", error);
    }
  },

  setParentCustomizationSettings: (data) => {
    set({ parentCustomizationSettings: data });
  },

  refreshSettings: async () => {
    try {
      const settings = await api.getCompanySettings();
      set({ companySettings: settings });
      get().calculateCurrencies();
    } catch (error) {
      console.error("Error refreshing settings:", error);
    }
  },

  refreshStripeDetails: async () => {
    try {
      const details = await api.getOnboardingDetails();
      set({ getStripeDetails: details?.data });
    } catch (error) {
      console.error("Error refreshing Stripe details:", error);
    }
  },

  refreshValidateStripeFields: async () => {
    try {
      const validation = await api.validateRequiredStripeFields();
      set({ validateStripeFields: validation });
    } catch (error) {
      console.error("Error refreshing validation:", error);
    }
  },

  calculateCurrencies: () => {
    const { companySettings } = get();
    if (!companySettings?.currencies) return;

    const { defaultCurrency, additionalCurrencies } =
      companySettings.currencies;
    const enabledCurrencies = [
      defaultCurrency,
      ...additionalCurrencies.filter((c) => c.isEnabled).map((c) => c.code),
    ];

    const currencyItems = Object.entries(CURRENCIES)
      .filter(([code]) => enabledCurrencies.includes(code as CurrencyCodes))
      .map(([code, currency]) => ({
        label: currency.name,
        currencyCode: code as CurrencyCodes,
        countryCode: currency.countryCode,
        symbol: currency.symbol,
        isDefaultCurrency: code === defaultCurrency,
      }));

    set({ currencyItemsForDropdown: currencyItems });
  },
}));

export const useCompanyStoreInit = () => {
  const {
    refreshCompany,
    refreshSettings,
    refreshStripeDetails,
    refreshValidateStripeFields,
  } = useCompanyStore();

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        refreshCompany(),
        refreshSettings(),
        refreshStripeDetails(),
        refreshValidateStripeFields(),
      ]);
    };
    init();
  }, []);
};
